import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, Spinner } from '@klaro7/ui';

import { useMarketingConsents } from '@/features/marketing-consents/hooks/useMarketingConsents';
import { downloadMarketingConsentsCsv } from '@/features/marketing-consents/services/marketing-consent.service';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ROLE_LABELS: Record<string, string> = {
  user: 'Cliente',
  professional: 'Consulente',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

const ROLE_FILTERS = [
  { value: '', label: 'Tutti i ruoli' },
  { value: 'user', label: 'Clienti' },
  { value: 'professional', label: 'Consulenti' },
];

export function AdminMarketingConsentsPage() {
  const [page, setPage] = useState(1);
  const [role, setRole] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const { data, isLoading, isError, isFetching } = useMarketingConsents(page, {
    q: q || undefined,
    role: role || undefined,
  });
  const items = data?.items ?? [];
  const pagination = data?.pagination;

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(searchInput.trim());
    setPage(1);
  }

  async function handleExport() {
    setExportError(null);
    setExporting(true);
    try {
      await downloadMarketingConsentsCsv({ q: q || undefined, role: role || undefined });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export fallito');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header banner */}
      <div className="rounded-xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Consensi Marketing</h1>
            <p className="mt-1 text-sm text-white/80">
              Utenti che hanno prestato il consenso alle comunicazioni promozionali.
              Base giuridica: consenso. Usa questi dati solo per campagne coerenti.
            </p>
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || isLoading || items.length === 0}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-orange/90 disabled:opacity-50"
          >
            {exporting ? 'Esporto…' : 'Esporta CSV'}
          </button>
        </div>
      </div>

      {exportError && (
        <Alert variant="error">
          <AlertDescription>{exportError}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => {
                setRole(f.value);
                setPage(1);
              }}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                role === f.value
                  ? 'bg-brand-teal text-white'
                  : 'border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <form onSubmit={submitSearch} className="flex items-center gap-2">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cerca per nome o email…"
            className="w-64 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
          />
          <button
            type="submit"
            className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            Cerca
          </button>
        </form>
      </div>

      {isError && (
        <Alert variant="error">
          <AlertDescription>Errore nel caricamento dei consensi. Riprova più tardi.</AlertDescription>
        </Alert>
      )}

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-neutral-500">
            Nessun utente con consenso marketing trovato.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-100 text-sm">
              <thead className="bg-neutral-50 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="px-4 py-3">Utente</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Ruolo</th>
                  <th className="px-4 py-3">Data consenso</th>
                  <th className="px-4 py-3">Versione privacy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {items.map((u) => (
                  <tr key={u.id} className="align-top">
                    <td className="whitespace-nowrap px-4 py-3">
                      <Link
                        to={`/dashboard/admin/users/${u.id}`}
                        className="font-medium text-brand-teal hover:underline"
                      >
                        {`${u.firstName} ${u.lastName}`.trim() || 'Utente'}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-neutral-800">{u.email}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
                        {ROLE_LABELS[u.role] ?? u.role}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-neutral-500">
                      {formatDateTime(u.marketingConsentAt)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-neutral-500">
                      {u.privacyVersion ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination && (pagination.totalPages > 1 || isFetching) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-500">
            {pagination.total} consensi · pagina {pagination.page} di {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!pagination.hasPrevPage}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Precedente
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!pagination.hasNextPage}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Successiva
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
